import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Autocomplete,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";

// ─── helpers ──────────────────────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatINR = (val) =>
  val != null
    ? Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

// ─── per-mode config ───────────────────────────────────────────────────────────
const MODE_CONFIG = {
  gstLiability: {
    title: "GST Liability — Bifurcated Details",
    footerFields: ["amount", "netGstLiability"],
    sheetName: "GST_Liability",
    columns: [
      { headerName: "Date", field: "tranDate", kind: "date", width: 130 },
      { headerName: "Type", field: "type", kind: "badge", width: 90 },
      { headerName: "Remarks", field: "remarks", kind: "text", flex: 2, minWidth: 220 },
      { headerName: "Amount (₹)", field: "amount", kind: "amount", width: 170 },
      { headerName: "Net GST Liability (₹)", field: "netGstLiability", kind: "amount", width: 200 },
    ],
  },
  igstLiability: {
    title: "IGST Liability — Bifurcated Details",
    footerFields: ["amount", "netGstLiability"],
    sheetName: "IGST_Liability",
    columns: [
      { headerName: "Date", field: "tranDate", kind: "date", width: 130 },
      { headerName: "Type", field: "type", kind: "badge", width: 90 },
      { headerName: "Remarks", field: "remarks", kind: "text", flex: 2, minWidth: 220 },
      { headerName: "Amount (₹)", field: "amount", kind: "amount", width: 170 },
      {
        headerName: "Net IGST Liability (₹)",
        field: "netGstLiability",
        kind: "amount",
        width: 210,
      },
    ],
  },
  gstPaid: {
    title: "GST Paid — Bifurcated Details",
    footerFields: ["gstPaidAmount"],
    sheetName: "GST_Paid",
    columns: [
      { headerName: "Date", field: "tranDate", kind: "date", width: 130 },
      { headerName: "Type", field: "type", kind: "badge", width: 90 },
      { headerName: "Remarks", field: "remarks", kind: "text", flex: 2, minWidth: 220 },
      { headerName: "GST Paid Amount (₹)", field: "gstPaidAmount", kind: "amount", width: 190 },
    ],
  },
  igstPaid: {
    title: "IGST Paid — Bifurcated Details",
    footerFields: ["gstPaidAmount"],
    sheetName: "IGST_Paid",
    columns: [
      { headerName: "Date", field: "tranDate", kind: "date", width: 130 },
      { headerName: "Type", field: "type", kind: "badge", width: 90 },
      { headerName: "Remarks", field: "remarks", kind: "text", flex: 2, minWidth: 220 },
      { headerName: "IGST Paid Amount (₹)", field: "gstPaidAmount", kind: "amount", width: 200 },
    ],
  },
  gstReceivable: {
    title: "Duties & Taxes ",
    footerFields: ["gstReceivable", "inputGSTAmount", "inputIGSTAmount", "totalGSTRecevable"],
    sheetName: "GST_Receivable",
    columns: [
      { headerName: "Date", field: "tranDate", kind: "date", width: 130 },
      { headerName: "Type", field: "type", kind: "badge", width: 90 },
      { headerName: "Remarks", field: "remarks", kind: "text", flex: 2, minWidth: 220 },
      { headerName: "GST Receivable (₹)", field: "gstReceivable", kind: "amount", width: 190 },
      { headerName: "Input GST Amount (₹)", field: "inputGSTAmount", kind: "amount", width: 190 },
      { headerName: "Input IGST Amount (₹)", field: "inputIGSTAmount", kind: "amount", width: 190 },
      {
        headerName: "Total GST Receivable (₹)",
        field: "totalGSTRecevable",
        kind: "amount",
        width: 210,
      },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
export default function GstDialogPopup({ open, onClose, loading, data, mode }) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.gstReceivable;
  const gridRef = useRef();

  const [filteredData, setFilteredData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);

  // ── Build AgGrid columnDefs from config ──────────────────────────────────
  const columnDefs = useMemo(
    () =>
      config.columns.map((col) => {
        const base = {
          headerName: col.headerName,
          field: col.field,
          sortable: true,
          filter: true,
          ...(col.width ? { width: col.width } : {}),
          ...(col.flex ? { flex: col.flex } : {}),
          ...(col.minWidth ? { minWidth: col.minWidth } : {}),
        };

        if (col.kind === "date") {
          return { ...base, valueFormatter: (p) => formatDate(p.value) };
        }

        if (col.kind === "badge") {
          return {
            ...base,
            cellRenderer: (p) => {
              if (!p.value) return "";
              const isCR = p.value === "CR";
              return (
                <span
                  style={{
                    backgroundColor: isCR ? "#e6f0ff" : "#ffe6e6",
                    color: isCR ? "#1a73e8" : "#d93025",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {p.value}
                </span>
              );
            },
          };
        }

        if (col.kind === "amount") {
          return {
            ...base,
            type: "numericColumn",
            valueFormatter: (p) => (p.value != null ? formatINR(p.value) : ""),
            cellStyle: (p) => {
              if (p.node.rowPinned === "bottom") {
                return { fontWeight: "bold", backgroundColor: "#FFF3E0", textAlign: "right" };
              }
              return {
                fontWeight: "bold",
                textAlign: "right",
                color: (p.value ?? 0) < 0 ? "#c62828" : "#2e7d32",
              };
            },
          };
        }

        return base; // text
      }),
    [config]
  );

  // ── Autocomplete options ─────────────────────────────────────────────────
  const columnOptions = useMemo(
    () => columnDefs.filter((c) => c.field).map((c) => ({ label: c.headerName, field: c.field })),
    [columnDefs]
  );

  // Auto-select first column when dialog opens
  useEffect(() => {
    if (open && columnOptions.length > 0) setSearchColumn(columnOptions[0]);
  }, [open]);

  // Sync incoming data
  useEffect(() => {
    setFilteredData(data || []);
  }, [data]);

  // Live filter
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredData(data || []);
      return;
    }
    const lower = searchText.toLowerCase();
    setFilteredData(
      (data || []).filter((row) => {
        const val = row[searchColumn.field];
        if (val == null) return false;
        if (typeof val === "number") return val.toString().includes(searchText);
        return val.toString().toLowerCase().includes(lower);
      })
    );
  }, [data, searchColumn, searchText]);

  // Pinned footer totals
  const calculateFooter = useCallback(
    (rows) => {
      const totals = { remarks: `${rows.length} entries` };
      config.footerFields.forEach((f) => {
        totals[f] = rows.reduce((s, r) => s + (r[f] != null ? Number(r[f]) : 0), 0);
      });
      return totals;
    },
    [config]
  );

  useEffect(() => {
    setPinnedBottomRowData([calculateFooter(filteredData)]);
  }, [filteredData, calculateFooter]);

  const handleClearSearch = () => {
    setSearchColumn(columnOptions[0] || null);
    setSearchText("");
  };

  // ── Excel export ─────────────────────────────────────────────────────────
  const handleDownloadExcel = () => {
    if (!filteredData || filteredData.length === 0) return;

    const exportData = filteredData.map((row) => {
      const obj = {};
      config.columns.forEach((col) => {
        let val = row[col.field];
        if (col.kind === "date" && val) val = formatDate(val);
        obj[col.headerName] = val ?? "";
      });
      return obj;
    });

    // Total footer row
    const totalObj = {};
    config.columns.forEach((col) => {
      if (col.kind === "amount") {
        totalObj[col.headerName] = filteredData.reduce(
          (s, r) => s + (r[col.field] != null ? Number(r[col.field]) : 0),
          0
        );
      } else if (col.field === "remarks") {
        totalObj[col.headerName] = `${filteredData.length} entries`;
      } else {
        totalObj[col.headerName] = col.field === "tranDate" ? "Total" : "";
      }
    });
    exportData.push(totalObj);

    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = config.columns.map((col) => ({
      wch: Math.max(col.headerName.length + 4, col.kind === "amount" ? 20 : 18),
    }));

    config.columns.forEach((col, colIdx) => {
      if (col.kind === "amount") {
        for (let r = 1; r <= exportData.length; r++) {
          const cell = XLSX.utils.encode_cell({ r, c: colIdx });
          if (ws[cell] && typeof ws[cell].v === "number") ws[cell].z = "#,##0.00";
        }
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.sheetName);
    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
        type: "application/octet-stream",
      }),
      `${config.sheetName}_Report.xlsx`
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#ffffff",
          borderRadius: 2,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          minHeight: "600px",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          bgcolor: "#282a2c",
          color: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#ffffff" }}>
          {config.title}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#ffffff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent
        sx={{
          p: 3,
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "400px",
        }}
      >
        {/* Search Controls */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, flexShrink: 0, mt: 0.1 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={columnOptions}
              getOptionLabel={(option) => option.label}
              value={searchColumn}
              onChange={(_, val) => {
                setSearchColumn(val);
                if (!val) setSearchText("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Column"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Text"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={
                searchColumn ? `Search in ${searchColumn.label}...` : "Select a column first"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                disabled={!searchText}
                size="small"
                sx={{ color: "#252121" }}
              >
                Clear Search
              </Button>
              <Typography variant="caption" color="text.secondary">
                {searchText && searchColumn
                  ? `Showing ${filteredData.length} of ${data.length} rows`
                  : `Total ${data.length} rows`}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Grid / States */}
        {loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}
          >
            <CircularProgress sx={{ color: "#f57c00" }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}
          >
            <Typography color="text.secondary">
              {searchText ? "No matching records found" : "No data available"}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: "300px",
              overflow: "hidden",
            }}
          >
            <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
              <AgGridReact
                ref={gridRef}
                rowData={filteredData}
                columnDefs={columnDefs}
                pinnedBottomRowData={pinnedBottomRowData}
                defaultColDef={{ resizable: true, filter: true, sortable: true }}
                getRowStyle={(params) => {
                  if (params.node.rowPinned === "bottom") {
                    return { fontWeight: "bold", backgroundColor: "#FFF3E0" };
                  }
                  return null;
                }}
                pagination={true}
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                headerHeight={48}
                rowHeight={44}
                suppressAggFuncInHeader={true}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
                onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
              />
            </div>
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}>
        <Button
          onClick={handleDownloadExcel}
          variant="contained"
          disabled={filteredData.length === 0}
          sx={{
            bgcolor: "#f57c00",
            "&:hover": { bgcolor: "#ffffff", color: "#f57c00", border: "1px solid #f57c00" },
            color: "#ffffff",
            mr: 1,
          }}
        >
          Download Excel
        </Button>
        <Button
          onClick={onClose}
          sx={{ color: "primary.main", "&:hover": { bgcolor: "rgba(255,152,0,0.1)" } }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: "#f57c00",
            "&:hover": { bgcolor: "#ffffff", color: "#f57c00", border: "1px solid #f57c00" },
            color: "#ffffff",
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

GstDialogPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  mode: PropTypes.string.isRequired,
};
